import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Link, useParams } from 'react-router-dom';
import { Chip, useTheme } from '@mui/material';
import { ClientService } from '../../../service/client.service';
import TaskTab from '../../shared/CustomDashboard/TaskTab/TaskTab';
import CustomHomepage from '../../shared/CustomHomePage/CustomHomepage';
import zeplinColor from '../../../theme/Color';
import TeamDetailsTab from './TeamDetailsTab';
import TeamAssignmentsTab from './TeamAssignmentsTab';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { setCurrentTab } from '../../../store/slices/teamUiSlice';
import styles from './TeamSelected.module.css';
import { Tasks } from 'pages/Dashboard/Tasks';
import { usePermissions } from 'customHook/usePermissions';

type DashboardTabItem = {
  label: string;
  Component: JSX.Element;
};

const TeamSelected = () => {
  const sf_redirect = sessionStorage.getItem('sf_redirect') || '';
  const dispatch = useAppDispatch();
  const selectedMember = useAppSelector((state) => state.teamUi.selectedMember) || {};
  const currentTab = useAppSelector((state) => state.teamUi.currentTab);
  const { hasAction, hasFullAccess } = usePermissions();
  const canViewTeamTasks =
    hasAction('Tasks', 'AllTasks', 'view') ||
    hasAction('Tasks', 'MyTasks', 'view') ||
    hasFullAccess('Tasks');
  const canViewTeamAssignments =
    hasAction('teams', 'assignments', 'view') || hasFullAccess('teams');
  const canViewTeamProfile = hasAction('teams', 'profile', 'view') || hasFullAccess('teams');
  const today = new Date();

  const formattedDate = today.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  // tab functionality
  const [value, setValue] = React.useState(0);
  const [alert, setAlert] = React.useState('');
  const [drawerState, setDrawerState] = React.useState(false);
  const onClose = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' ||
        (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    setDrawerState(open);
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    dispatch(setCurrentTab(newValue));
    setValue(newValue);
  };

  const handleClose = () => {
    setAlert('');
  };

  const dashboardTabItems: DashboardTabItem[] = [
    ...(canViewTeamProfile
      ? [
          {
            label: 'Profile Details',
            Component: (
              <Box>
                <TeamDetailsTab />
              </Box>
            ),
          },
        ]
      : []),

    ...(canViewTeamTasks
      ? [
          {
            label: 'Tasks',
            Component: (
              <Box>
                <Tasks teamScope={{ teamId: selectedMember.id, teamName: selectedMember?.name }} />
              </Box>
            ),
          },
        ]
      : []),

    ...(canViewTeamAssignments
      ? [
          {
            label: 'Assignments',
            Component: (
              <Box>
                <TeamAssignmentsTab />
              </Box>
            ),
          },
        ]
      : []),
  ];

  const DashboardTabs = dashboardTabItems.map((tab, index) => ({
    ...tab,
    id: index + 1,
    isHidden: value !== index,
  }));

  React.useEffect(() => {
    let nextValue = 0;

    if (sf_redirect === 'Task') {
      const tasksTabIndex = dashboardTabItems.findIndex((tab) => tab.label === 'Tasks');
      nextValue = tasksTabIndex !== -1 ? tasksTabIndex : 0;
    } else if (currentTab != null) {
      nextValue = Math.min(Math.max(currentTab, 0), Math.max(dashboardTabItems.length - 1, 0));
    }

    setValue(nextValue);
  }, [sf_redirect, currentTab, dashboardTabItems.length]);

  return (
    <>
      <CustomHomepage
        sportsData={[]}
        ProfileDetails={
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flex: 1,
            }}
          >
            <Box sx={{ display: 'flex', gap: '16px', flex: 1 }}>
              <Box
                sx={{
                  width: { xs: '80px', xl: '100px' },
                  height: { xs: '80px', xl: '100px' },
                  borderRadius: '14px',
                  backgroundColor: zeplinColor.Brand1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Typography variant='responsiveDisplay4' sx={{ color: zeplinColor.Neutral1 }}>
                  {selectedMember?.name
                    ?.split(' ')
                    .map((val: any, index: number) =>
                      index === 0 || index === selectedMember?.name?.split(' ').length - 1
                        ? val[0]
                        : '',
                    )}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  // gap: '16px',
                  justifyContent: 'space-between',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                  }}
                >
                  {' '}
                  <Typography
                    sx={{ fontSize: { xs: '24px', xl: '36px' }, fontWeight: '300' }}
                    color={zeplinColor.Neutral1}
                  >
                    {selectedMember?.name ?? 'N/A'}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <Chip
                      label={
                        <Typography
                          variant='responsiveLabel1'
                          color={zeplinColor.Neutral1}
                          sx={{ display: 'flex', alignItems: 'center' }}
                        >
                          {selectedMember?.position ?? 'N/A'}
                        </Typography>
                      }
                      sx={{
                        backgroundColor: zeplinColor.Neutral10,
                        padding: '5px 12px 5px 12px',
                        borderRadius: '50px',
                        // width: '47px',
                        height: '24px',
                        '&.MuiChip-root': {
                          // flex: 1,
                        },
                      }}
                    />

                    {selectedMember?.title && (
                      <Chip
                        label={
                          <Typography
                            variant='responsiveLabel1'
                            color={zeplinColor.Neutral1}
                            sx={{ display: 'flex', alignItems: 'center' }}
                          >
                            {selectedMember?.title ?? 'N/A'}
                          </Typography>
                        }
                        sx={{
                          backgroundColor: zeplinColor.Neutral10,
                          padding: '5px 12px 5px 12px',
                          borderRadius: '50px',
                          // width: '47px',
                          height: '24px',
                          '&.MuiChip-root': {
                            // flex: 1,
                          },
                        }}
                      />
                    )}
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '32px',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    {' '}
                    <img
                      src='/assets/email.svg'
                      className={styles.icon}
                      style={{ objectFit: 'fill' }}
                    />
                    <a
                      href={`mailto:${selectedMember?.email}`}
                      target='_blank'
                      rel='noopener noreferrer'
                      className={styles['contact-text']}
                      style={{ color: zeplinColor.Brand1, textDecoration: 'none' }}
                    >
                      {selectedMember?.email ?? 'N/A'}
                    </a>
                  </Box>
                  <Box
                    className={styles['contact-text']}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    {' '}
                    <img
                      src='/assets/mobile.svg'
                      className={styles.icon}
                      style={{ objectFit: 'fill' }}
                    />
                    {selectedMember?.mobile || selectedMember?.phone
                      ? selectedMember?.mobile || selectedMember?.phone
                      : 'N/A'}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        }
        DealCard={<Box></Box>}
        DashboardTabs={DashboardTabs}
        handleChange={handleChange}
        value={value}
        alert={alert}
        handleClose={handleClose}
        Children={''}
      />
    </>
  );
};

export default TeamSelected;
